import React, { useState, useEffect } from 'react';
import { usersAPI } from '../../services/api';
import { Button } from '../ui';
import { useLanguage } from '../../contexts/LanguageContext';
import { handleApiError } from '../../utils/helpers';

const UserFormModal = ({ user, onClose, onSuccess }) => {
  const { isRTL, t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'designer',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        email: user.email || '',
        password: '', // Don't show password for existing user
        role: user.role || 'designer',
      });
    }
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const submitData = {
        name: formData.name,
        email: formData.email,
        role: formData.role,
      };

      if (user) {
        // Update existing user
        await usersAPI.update(user.id, submitData);
      } else {
        // Create new user
        if (!formData.password || formData.password.length < 6) {
          throw new Error(isRTL ? 'كلمة المرور مطلوبة (6 أحرف على الأقل)' : 'Password is required (minimum 6 characters)');
        }
        await usersAPI.create({
          ...submitData,
          password: formData.password,
        });
      }
      onSuccess();
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">
              {user ? t('editTeamMember') : t('addTeamMember')}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl"
            >
              ×
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}

            {/* Name */}
            <div>
              <label htmlFor="name" className={`block text-sm font-medium text-gray-700 mb-2 ${isRTL ? 'text-right' : 'text-left'}`}>
                {t('name')} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                required
                value={formData.name}
                onChange={handleChange}
                placeholder={isRTL ? 'مثال: أحمد محمد' : 'e.g., John Doe'}
                className={`input w-full ${isRTL ? 'text-right' : 'text-left'}`}
                dir={isRTL ? 'rtl' : 'ltr'}
              />
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className={`block text-sm font-medium text-gray-700 mb-2 ${isRTL ? 'text-right' : 'text-left'}`}>
                {t('email')} <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                required
                value={formData.email}
                onChange={handleChange}
                placeholder="user@example.com"
                className={`input w-full ${isRTL ? 'text-right' : 'text-left'}`}
                dir="ltr"
              />
            </div>

            {/* Password (only when creating) */}
            {!user && (
              <div>
                <label htmlFor="password" className={`block text-sm font-medium text-gray-700 mb-2 ${isRTL ? 'text-right' : 'text-left'}`}>
                  {t('password')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  placeholder={isRTL ? '6 أحرف على الأقل' : 'Minimum 6 characters'}
                  minLength="6"
                  className={`input w-full ${isRTL ? 'text-right' : 'text-left'}`}
                  dir="ltr"
                />
                <p className={`text-xs text-gray-500 mt-1 ${isRTL ? 'text-right' : 'text-left'}`}>
                  {t('passwordLoginTip')}
                </p>
              </div>
            )}

            {/* Role */}
            <div>
              <label htmlFor="role" className={`block text-sm font-medium text-gray-700 mb-2 ${isRTL ? 'text-right' : 'text-left'}`}>
                {t('roleColumn')} <span className="text-red-500">*</span>
              </label>
              <select
                id="role"
                name="role"
                required
                value={formData.role}
                onChange={handleChange}
                className={`input w-full ${isRTL ? 'text-right' : 'text-left'}`}
                dir={isRTL ? 'rtl' : 'ltr'}
              >
                <option value="designer">{t('designer')}</option>
                <option value="writer">{t('writer')}</option>
                <option value="seo_specialist">{t('seoSpecialist')}</option>
                <option value="team_manager">{t('teamManager')}</option>
              </select>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-3 pt-4 border-t justify-end">
              <Button type="button" variant="secondary" onClick={onClose} disabled={loading}>
                {t('cancel')}
              </Button>
              <Button type="submit" variant="primary" disabled={loading}>
                {loading ? t('saving') : (user ? t('updateUser') : t('createUser'))}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UserFormModal;







